import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Star, Plus, Trash2, Edit, Loader2, Save, ArrowLeft, Eye, EyeOff,
  ExternalLink, Image, Video, Users, Radio, Link2, LayoutGrid,
  ChevronDown, ChevronRight, RefreshCw, Search, X, GripVertical
} from 'lucide-react';

const TYPES = [
  { value: 'creator', label: 'Creator', icon: Users },
  { value: 'post', label: 'Post', icon: LayoutGrid },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'live_replay', label: 'Live Replay', icon: Radio },
  { value: 'external_link', label: 'External Link', icon: Link2 },
  { value: 'banner', label: 'Banner', icon: Image },
];

export default function AdminSpotlightPage() {
  const { user, getAuthHeaders, isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const isAdmin = isAuthenticated && (user?.role === 'owner' || user?.role === 'admin');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/spotlight-items`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { if (isAdmin) fetchItems(); }, [isAdmin, fetchItems]);

  const deleteItem = async (id) => {
    if (!confirm('Delete this spotlight item?')) return;
    const res = await fetch(`${API}/admin/spotlight-items/${id}`, {
      method: 'DELETE', headers: getAuthHeaders()
    });
    if (res.ok) { toast.success('Deleted'); fetchItems(); }
    else toast.error('Failed to delete');
  };

  const toggleActive = async (item) => {
    const res = await fetch(`${API}/admin/spotlight-items/${item.spotlight_item_id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !item.is_active })
    });
    if (res.ok) { toast.success(item.is_active ? 'Deactivated' : 'Activated'); fetchItems(); }
  };

  if (!isAdmin) return <div className="p-8 text-center text-muted-foreground">Admin access required</div>;

  const activeItems = items.filter(i => i.is_active);
  const inactiveItems = items.filter(i => !i.is_active);

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-spotlight-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin/control-center" className="text-muted-foreground hover:text-foreground flex items-center gap-2 mb-2 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <div className="flex items-center gap-3">
              <Star className="w-7 h-7 text-yellow-500" />
              <h1 className="text-2xl font-bold">Soundstage Spotlight</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Manage featured content on the Spotlight page</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchItems} data-testid="refresh-spotlight">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Link to="/spotlight" target="_blank">
              <Button variant="outline" size="sm" data-testid="preview-spotlight">
                <Eye className="w-4 h-4 mr-2" /> Preview
              </Button>
            </Link>
            <Button size="sm" onClick={() => { setEditingItem(null); setShowForm(true); }} data-testid="create-spotlight-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Item
            </Button>
          </div>
        </div>

        {/* Warning if no active items */}
        {!loading && activeItems.length === 0 && (
          <Card className="p-4 border-yellow-500/30 bg-yellow-500/5">
            <p className="text-sm text-yellow-400 font-medium">No active spotlight items. The Spotlight page will appear empty to users.</p>
          </Card>
        )}

        {/* Create/Edit Form */}
        {showForm && (
          <SpotlightForm
            item={editingItem}
            getAuthHeaders={getAuthHeaders}
            onSuccess={() => { setShowForm(false); setEditingItem(null); fetchItems(); }}
            onCancel={() => { setShowForm(false); setEditingItem(null); }}
          />
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            {/* Active Items */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Eye className="w-5 h-5 text-green-500" />
                Active ({activeItems.length})
              </h2>
              {activeItems.length === 0 ? (
                <Card className="p-6 text-center text-muted-foreground text-sm">No active items</Card>
              ) : (
                <div className="space-y-2">
                  {activeItems.sort((a, b) => a.priority - b.priority).map(item => (
                    <SpotlightRow
                      key={item.spotlight_item_id}
                      item={item}
                      onEdit={() => { setEditingItem(item); setShowForm(true); }}
                      onDelete={() => deleteItem(item.spotlight_item_id)}
                      onToggle={() => toggleActive(item)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Inactive Items */}
            {inactiveItems.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <EyeOff className="w-5 h-5 text-muted-foreground" />
                  Inactive ({inactiveItems.length})
                </h2>
                <div className="space-y-2">
                  {inactiveItems.map(item => (
                    <SpotlightRow
                      key={item.spotlight_item_id}
                      item={item}
                      onEdit={() => { setEditingItem(item); setShowForm(true); }}
                      onDelete={() => deleteItem(item.spotlight_item_id)}
                      onToggle={() => toggleActive(item)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SpotlightRow({ item, onEdit, onDelete, onToggle }) {
  const TypeIcon = TYPES.find(t => t.value === item.type)?.icon || Star;
  return (
    <Card className={`p-3 flex items-center gap-3 ${item.is_active ? 'border-green-500/20' : 'opacity-60'}`} data-testid={`spotlight-item-${item.spotlight_item_id}`}>
      {/* Thumbnail */}
      <div className="w-16 h-12 rounded bg-muted overflow-hidden flex-shrink-0">
        {item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><TypeIcon className="w-5 h-5 text-muted-foreground" /></div>
        )}
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{item.title}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-xs capitalize">{item.type.replace('_', ' ')}</Badge>
          <Badge variant="outline" className={`text-xs ${item.visibility === 'public' ? 'text-green-400 border-green-400/30' : 'text-blue-400 border-blue-400/30'}`}>
            {item.visibility === 'public' ? 'Public' : 'Followers'}
          </Badge>
          <span>Priority: {item.priority}</span>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Switch checked={item.is_active} onCheckedChange={onToggle} />
        <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-${item.spotlight_item_id}`}><Edit className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" onClick={onDelete} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
      </div>
    </Card>
  );
}

function SpotlightForm({ item, getAuthHeaders, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    title: item?.title || '',
    subtitle: item?.subtitle || '',
    type: item?.type || 'creator',
    creator_id: item?.creator_id || '',
    content_id: item?.content_id || '',
    external_url: item?.external_url || '',
    thumbnail_url: item?.thumbnail_url || '',
    hero_url: item?.hero_url || '',
    cta_text: item?.cta_text || '',
    cta_url: item?.cta_url || '',
    is_active: item?.is_active ?? true,
    visibility: item?.visibility || (item?.type && ['external_link', 'banner'].includes(item.type) ? 'public' : 'followers_only'),
    priority: item?.priority ?? 100,
    start_at: item?.start_at || '',
    end_at: item?.end_at || '',
  });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const needsCreator = ['creator'].includes(form.type);
  const needsContent = ['post', 'video', 'live_replay'].includes(form.type);
  const needsUrl = ['external_link'].includes(form.type);

  const searchCreators = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/search/users?q=${encodeURIComponent(q)}&limit=8`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || []);
      }
    } catch { /* */ }
    finally { setSearching(false); }
  }, [getAuthHeaders]);

  useEffect(() => {
    const t = setTimeout(() => searchCreators(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchCreators]);

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const url = item
        ? `${API}/admin/spotlight-items/${item.spotlight_item_id}`
        : `${API}/admin/spotlight-items`;
      const res = await fetch(url, {
        method: item ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(item ? 'Updated!' : 'Created!');
        onSuccess();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 space-y-4 border-primary/20" data-testid="spotlight-form">
      <h3 className="font-semibold flex items-center gap-2">
        <Star className="w-5 h-5 text-yellow-500" />
        {item ? 'Edit Spotlight Item' : 'Create Spotlight Item'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="spotlight-title" />
        <Input placeholder="Subtitle (optional)" value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} />
      </div>

      {/* Type Selector */}
      <div className="flex flex-wrap gap-2">
        {TYPES.map(t => (
          <Button
            key={t.value}
            variant={form.type === t.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setForm(f => ({
              ...f, type: t.value,
              visibility: ['external_link', 'banner'].includes(t.value) ? 'public' : 'followers_only'
            }))}
            data-testid={`type-${t.value}`}
          >
            <t.icon className="w-4 h-4 mr-1" /> {t.label}
          </Button>
        ))}
      </div>

      {/* Creator Search */}
      {needsCreator && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search creator by name or handle..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="creator-search"
          />
          {form.creator_id && (
            <Badge className="mt-1">Creator: {form.creator_id} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setForm(f => ({ ...f, creator_id: '' }))} /></Badge>
          )}
          {searchResults.length > 0 && (
            <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-card border rounded-lg shadow-xl max-h-48 overflow-y-auto">
              {searchResults.map(u => (
                <button
                  key={u.user_id}
                  className="w-full flex items-center gap-3 p-2 hover:bg-muted/30 text-left text-sm"
                  onClick={() => {
                    setForm(f => ({
                      ...f,
                      creator_id: u.user_id,
                      owner_creator_id: u.user_id,
                      thumbnail_url: f.thumbnail_url || u.picture || u.avatar_url || '',
                    }));
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                >
                  <div className="w-8 h-8 rounded-full bg-muted overflow-hidden">
                    {(u.picture || u.avatar_url) && <img src={u.picture || u.avatar_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div>
                    <p className="font-medium">{u.name || u.stage_name}</p>
                    <p className="text-xs text-muted-foreground">{u.handle ? `@${u.handle}` : ""} · {u.role}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content ID */}
      {needsContent && (
        <Input placeholder="Content ID (post_id, video_id, or replay_id)" value={form.content_id} onChange={e => setForm(f => ({ ...f, content_id: e.target.value }))} data-testid="content-id" />
      )}

      {/* External URL */}
      {needsUrl && (
        <Input placeholder="External URL *" value={form.external_url} onChange={e => setForm(f => ({ ...f, external_url: e.target.value }))} data-testid="external-url" />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input placeholder="Thumbnail URL" value={form.thumbnail_url} onChange={e => setForm(f => ({ ...f, thumbnail_url: e.target.value }))} data-testid="spotlight-thumbnail" />
        <Input placeholder="Hero image URL (optional)" value={form.hero_url} onChange={e => setForm(f => ({ ...f, hero_url: e.target.value }))} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input placeholder="CTA text (e.g. Watch)" value={form.cta_text} onChange={e => setForm(f => ({ ...f, cta_text: e.target.value }))} />
        <Input placeholder="CTA link (auto-generated if blank)" value={form.cta_url} onChange={e => setForm(f => ({ ...f, cta_url: e.target.value }))} />
        <Input type="number" placeholder="Priority (lower = higher)" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 100 }))} />
      </div>

      {/* Visibility + Active */}
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Visibility:</Label>
          <Button variant={form.visibility === 'followers_only' ? 'default' : 'outline'} size="sm" onClick={() => setForm(f => ({ ...f, visibility: 'followers_only' }))}>Followers Only</Button>
          <Button variant={form.visibility === 'public' ? 'default' : 'outline'} size="sm" onClick={() => setForm(f => ({ ...f, visibility: 'public' }))}>Public</Button>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
          <Label className="text-sm">Active</Label>
        </div>
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Start Date (optional)</Label>
          <Input type="datetime-local" value={form.start_at?.slice(0, 16) || ''} onChange={e => setForm(f => ({ ...f, start_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">End Date (optional)</Label>
          <Input type="datetime-local" value={form.end_at?.slice(0, 16) || ''} onChange={e => setForm(f => ({ ...f, end_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))} />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving || !form.title.trim()} data-testid="submit-spotlight">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {item ? 'Update' : 'Create'}
        </Button>
      </div>
    </Card>
  );
}
